import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import * as TokenService from '../../../network/token'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  avatarUrl?: string | null
  organizationId?: string | null
}

interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

interface AuthState {
  // State
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  _hasHydrated: boolean

  // Actions
  setAuth: (user: User, tokens: AuthTokens) => void
  setTokens: (tokens: AuthTokens) => void
  setUser: (user: User | null) => void
  clearAuth: () => void
  updateTokens: (accessToken: string, refreshToken?: string) => void
  _setHasHydrated: (state: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      _hasHydrated: false,

      // Set complete authentication (login)
      setAuth: (user: User, tokens: AuthTokens) => {
        console.log('🔐 [authStore] setAuth called:', {
          userId: user.id,
          email: user.email,
          hasAccessToken: !!tokens.accessToken
        });

        // Store tokens in TokenService for HTTP interceptors
        TokenService.setTokens(tokens.accessToken, tokens.refreshToken)

        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        })

        console.log('🔐 [authStore] setAuth complete, new state:', {
          isAuthenticated: true,
          userId: user.id
        });
      },

      // Set tokens only (useful for refresh)
      setTokens: (tokens: AuthTokens) => {
        // Store tokens in TokenService for HTTP interceptors
        TokenService.setTokens(tokens.accessToken, tokens.refreshToken)

        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        })
      },

      // Set user data only
      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user && !!get().accessToken,
        })
      },

      // Clear all auth data (logout)
      clearAuth: () => {
        console.log('🔐 [authStore] clearAuth called');

        // Remove tokens from TokenService
        TokenService.removeTokens()

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })

        console.log('🔐 [authStore] clearAuth complete, new state:', {
          isAuthenticated: false,
          user: null
        });
      },

      // Update tokens (for refresh operations)
      updateTokens: (accessToken: string, refreshToken?: string) => {
        const currentRefreshToken = get().refreshToken
        const finalRefreshToken = refreshToken || currentRefreshToken

        // Store tokens in TokenService for HTTP interceptors
        if (finalRefreshToken) {
          TokenService.setTokens(accessToken, finalRefreshToken)
        }

        set({
          accessToken,
          refreshToken: finalRefreshToken,
          isAuthenticated: true,
        })
      },

      // Internal: Mark store as hydrated
      _setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state })
      },
    }),
    {
      name: 'bodywave-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        console.log('🔐 [authStore] Starting rehydration from localStorage');
        return (state) => {
          console.log('🔐 [authStore] Rehydration complete:', {
            hasState: !!state,
            isAuthenticated: state?.isAuthenticated,
            hasUser: !!state?.user,
            hasAccessToken: !!state?.accessToken
          });

          // CRITICAL: Synchronize tokens with TokenService after rehydration
          // This prevents race condition where HTTP interceptors try to use tokens
          // before TokenService is populated
          if (state?.accessToken && state?.refreshToken) {
            console.log('🔐 [authStore] Syncing tokens with TokenService after hydration');
            TokenService.setTokens(state.accessToken, state.refreshToken);
          }

          // Mark as hydrated after rehydration completes
          state?._setHasHydrated(true)
        }
      },
    }
  )
)

// Utility selector hooks for convenience
export const useAccessToken = () => useAuthStore((state) => state.accessToken)
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
